import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import { Card, Button, Form, Row, Col, InputGroup } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaHistory, FaSearch, FaEye, FaFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";

// Import the layout components and styles
import DepartmentTopbar from "../SuperAdmin/components/DepartmentTopbar";
import DepartmentSidebar from "../SuperAdmin/components/DepertmentSidebar";
import styles from "../../assets/css/Dashboard.module.css";

// Import Modal components
import RegisterModal from "./RegisterModal";
import RenewModal from "./RenewModal";
import TradeLicenseDetailsModal from "./TradeLicenseDetailsModal";

function TradeLicenseList() {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [expiryFilter, setExpiryFilter] = useState("all");

  const API_URL = process.env.REACT_APP_SUPER_ADMIN_API_URL;
  const token = localStorage.getItem("token");

  const fetchTradeLicenses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}list-trade-license.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success && Array.isArray(response.data.data)) {
        setLicenses(response.data.data);
      } else {
        setLicenses([]);
      }
    } catch (error) {
      toast.error("Failed to fetch trade licenses.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTradeLicenses();
  }, []);

  const handleRegisterClick = (row) => {
    setSelectedLicense(row);
    setShowRegisterModal(true);
  };

  const handleRenewClick = (row) => {
    setSelectedLicense(row);
    setShowRenewModal(true);
  };

  const handleEditClick = (row) => {
    navigate(`/departmentDashboard/update-trade-license/${row.id}`);
  };

  const handleHistoryClick = (row) => {
    navigate(`/departmentDashboard/trade-license-history/${row.id}`);
  };

  const handleDetailsClick = (row) => {
    setSelectedLicense(row);
    setShowDetailsModal(true);
  };

  const isExpired = (dateStr) => {
    if (!dateStr || dateStr === "0000-00-00") return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateStr) < today;
  };

  const filteredLicenses = useMemo(() => {
    let filtered = licenses;
    if (searchTerm) {
      filtered = filtered.filter(
        (license) =>
          license.trade_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          license.trade_owner.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (expiryFilter !== "all") {
      filtered = filtered.filter((license) => {
        if (expiryFilter === "available") {
          return license.registration_status === "Available for registration";
        }
        const expired = isExpired(license.expire_date);
        if (expiryFilter === "expired") return expired;
        if (expiryFilter === "active")
          return (
            !expired && license.registration_status === "Already registered"
          );
        return true;
      });
    }
    return filtered;
  }, [licenses, searchTerm, expiryFilter]);

  const handleExport = () => {
    const formattedData = filteredLicenses.map((license) => ({
      ID: license.id,
      "Trade Name": license.trade_name,
      "Owner Name": license.trade_owner,
      Address: license.address,
      "Holding No": license.holding_no,
      Phone: license.phone,
      "Trade Type": license.trade_type_name,
      "Approx Area": license.approx_area,
      "Registration No": license.reg_no,
      "Registration Date": license.reg_date,
      "Expire Date": license.expire_date,
      Status: isExpired(license.expire_date)
        ? "Expired"
        : license.registration_status === "Already registered"
        ? "Active"
        : "Available for registration",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "TradeLicenses");
    XLSX.writeFile(workbook, "TradeLicenseReport.xlsx");
  };

  const columns = [
    {
      name: "Reg. No.",
      selector: (row) => row.reg_no || "N/A",
      sortable: true,
      width: "120px",
    },
    {
      name: "Trade Name",
      selector: (row) => row.trade_name,
      sortable: true,
      grow: 2,
    },
    {
      name: "Owner Name",
      selector: (row) => row.trade_owner,
      sortable: true,
      grow: 2,
    },
    {
      name: "Expire Date",
      selector: (row) => row.expire_date,
      sortable: true,
      cell: (row) => {
        if (!row.expire_date || row.expire_date === "0000-00-00") {
          return <span className="text-muted">N/A</span>;
        }
        return (
          <span
            className={
              isExpired(row.expire_date)
                ? "text-danger fw-bold"
                : "text-success"
            }
          >
            {row.expire_date}
          </span>
        );
      },
    },
    {
      name: "Status",
      sortable: true,
      cell: (row) => {
        const expired = isExpired(row.expire_date);
        let statusText = row.registration_status;
        let badgeColor = "secondary";

        if (expired) {
          statusText = "Expired";
          badgeColor = "danger";
        } else if (row.registration_status === "Already registered") {
          statusText = "Active";
          badgeColor = "success";
        }

        return <span className={`badge bg-${badgeColor}`}>{statusText}</span>;
      },
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex flex-wrap">
          <Button
            variant="primary"
            size="sm"
            className="m-1"
            onClick={() => handleDetailsClick(row)}
          >
            <FaEye />
          </Button>
          <Button
            variant="warning"
            size="sm"
            className="m-1"
            onClick={() => handleEditClick(row)}
          >
            Edit
          </Button>
          {row.registration_status === "Available for registration" ? (
            <Button
              variant="success"
              size="sm"
              className="m-1"
              onClick={() => handleRegisterClick(row)}
            >
              Register
            </Button>
          ) : (
            <>
              <Button
                variant="info"
                size="sm"
                className="m-1"
                onClick={() => handleRenewClick(row)}
              >
                Renew
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="m-1"
                onClick={() => handleHistoryClick(row)}
              >
                <FaHistory />
              </Button>
            </>
          )}
        </div>
      ),
      minWidth: "240px",
    },
  ];

  const subHeaderComponent = useMemo(() => {
    return (
      <Row className="g-3 w-100 my-2 align-items-center">
        <Col md={5}>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search by Trade Name or Owner Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={4}>
          <Form.Select
            value={expiryFilter}
            onChange={(e) => setExpiryFilter(e.target.value)}
          >
            <option value="all">Filter by Status: All</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="available">Available for Registration</option>
          </Form.Select>
        </Col>
        <Col md={3} className="text-md-end">
          <Button onClick={handleExport} variant="outline-success" size="sm">
            <FaFileExcel className="me-2" />
            Export to Excel
          </Button>
        </Col>
      </Row>
    );
  }, [searchTerm, expiryFilter, filteredLicenses]);

  return (
    <>
      <DepartmentTopbar />
      <div className="d-flex" style={{ minHeight: "calc(100vh - 56px)" }}>
        <DepartmentSidebar />
        <div className="flex-grow-1 p-3" style={{ background: "#f0f2f5" }}>
          <Card
            className="shadow-sm"
            style={{ border: "none", borderRadius: "18px", background: "#fff" }}
          >
            <Card.Header
              style={{
                background: "#fff",
                borderBottom: "1px solid #eee",
                padding: "20px",
              }}
            >
              <h5 className="m-0 font-weight-bold text-primary">
                All Trade Licenses
              </h5>
            </Card.Header>
            <Card.Body style={{ padding: "0" }}>
              <DataTable
                columns={columns}
                data={filteredLicenses}
                pagination
                highlightOnHover
                responsive
                progressPending={loading}
                subHeader
                subHeaderComponent={subHeaderComponent}
              />
            </Card.Body>
          </Card>

          <TradeLicenseDetailsModal
            show={showDetailsModal}
            handleClose={() => setShowDetailsModal(false)}
            licenseId={selectedLicense ? selectedLicense.id : null}
          />

          {selectedLicense && (
            <>
              <RegisterModal
                show={showRegisterModal}
                handleClose={() => setShowRegisterModal(false)}
                license={selectedLicense}
                onRegisterSuccess={fetchTradeLicenses}
              />
              <RenewModal
                show={showRenewModal}
                handleClose={() => setShowRenewModal(false)}
                license={selectedLicense}
                onRenewSuccess={fetchTradeLicenses}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default TradeLicenseList;
